import { useEffect, useMemo, useState } from "react";
import { api, inr, formatErr, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo";
import PWAInstallButton from "@/components/PWAInstallButton";
import PWAInstallNudge from "@/components/PWAInstallNudge";
import AllergenBadge from "@/components/AllergenBadge";
import {
  ChefHat, Plus, Minus, ShoppingBag, ChevronRight, CheckCircle2, ArrowLeft,
  Wallet, ScanLine, Hash, Store, GraduationCap, Briefcase, UserCircle2,
  KeyRound, ShieldCheck, CreditCard, Coins, BadgeIndianRupee, Loader2, X, Printer,
} from "lucide-react";
import axios from "axios";

const QR_TOKEN_KEY = "grubpe_qr_token";
const QR_USER_KEY = "grubpe_qr_user";
const QR_REMEMBER_KEY = "grubpe_qr_remember";   // "1" if user opted in; persists across browser sessions
const QR_TOKEN_EXP_KEY = "grubpe_qr_token_exp"; // ISO of expiry — for our local check (server still validates JWT exp)

/** Top-level QR ordering surface — supports four flows:
 * - card        → ?rfid=…   pre-auth wallet (no login)
 * - guest       → parent / walk-in pays via Cashfree, no login
 * - student     → student id + parent phone OTP → wallet OR cashfree
 * - staff       → staff id + phone OTP → points (with cash remainder) OR cashfree
 */
export default function QR() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const tableNumber = params.get("table");
  const rfidParam = params.get("rfid");
  const vendorParam = params.get("vendor");
  const schoolParam = params.get("school");

  const initialFlow = rfidParam ? "card" : (params.get("flow") || "pick");
  const [flow, setFlow] = useState(initialFlow);
  const [qrSession, setQrSession] = useState(() => {
    try {
      const tok = localStorage.getItem(QR_TOKEN_KEY);
      const u = JSON.parse(localStorage.getItem(QR_USER_KEY) || "null");
      const exp = localStorage.getItem(QR_TOKEN_EXP_KEY);
      // Soft expiry check — clear stale local copies (server JWT also validates exp)
      if (tok && u && exp && new Date(exp) > new Date()) return { token: tok, user: u };
      if (tok) {
        localStorage.removeItem(QR_TOKEN_KEY);
        localStorage.removeItem(QR_USER_KEY);
        localStorage.removeItem(QR_TOKEN_EXP_KEY);
      }
      return null;
    } catch { return null; }
  });

  // Cart state
  const [menu, setMenu] = useState([]);
  const [cats, setCats] = useState(["All"]);
  const [active, setActive] = useState("All");
  const [cart, setCart] = useState({});
  const [step, setStep] = useState("menu"); // "menu" | "checkout" | "success"
  const [orderResult, setOrderResult] = useState(null);
  const [vendorMeta, setVendorMeta] = useState(null);
  const [cardStudent, setCardStudent] = useState(null);
  const [guestForm, setGuestForm] = useState({ customer_name: "", customer_phone: "", customer_email: "" });
  const [paying, setPaying] = useState(false);
  const [qrPlanInfo, setQrPlanInfo] = useState(null); // {plan_name, used_count, days_remaining, plan_type}
  const [qrPreorders, setQrPreorders] = useState([]); // upcoming pre-orders
  const [qrRecentPurchases, setQrRecentPurchases] = useState([]); // recent completed orders
  const [searchQuery, setSearchQuery] = useState("");

  const [qrSchool, setQrSchool] = useState(null); // school config for the QR context

  useEffect(() => {
    const qp = new URLSearchParams();
    if (vendorParam) qp.set("vendor_id", vendorParam);
    if (schoolParam) qp.set("school", schoolParam);
    const url = `/menu?${qp.toString()}`;
    api.get(url).then(r => setMenu(r.data));
    api.get("/menu/categories" + (schoolParam ? `?school=${schoolParam}` : "")).then(r => setCats(["All", ...r.data]));
    if (vendorParam) {
      api.get("/vendors").then(r => setVendorMeta(r.data.find(v => v.id === vendorParam)));
    }
    // Fetch school config for QR context
    if (schoolParam) {
      api.get(`/schools/by-slug/${schoolParam}`).then(r => setQrSchool(r.data)).catch(() => {});
    }
  }, [vendorParam, schoolParam]);

  // Fetch meal plan info and pre-orders when QR session is active
  useEffect(() => {
    if (!qrSession?.token) { setQrPlanInfo(null); setQrPreorders([]); setQrRecentPurchases([]); return; }
    const headers = { Authorization: `Bearer ${qrSession.token}` };
    axios.get(`${API}/meal-plans/student/${qrSession.user?.id}`, { headers })
      .then(r => {
        const today = new Date().toISOString().slice(0, 10);
        const active = (r.data || []).find(sp => sp.status === "active" && sp.start_date <= today && sp.end_date >= today);
        setQrPlanInfo(active || null);
      }).catch(() => setQrPlanInfo(null));
    axios.get(`${API}/orders/me`, { headers })
      .then(r => {
        const today = new Date().toISOString().slice(0, 10);
        const upcoming = (r.data || []).filter(o => o.type === "preorder" && o.status !== "cancelled" && o.scheduled_date >= today);
        setQrPreorders(upcoming.slice(0, 5));
        // Recent completed purchases (last 5)
        const recent = (r.data || []).filter(o => o.status === "delivered" || o.status === "completed");
        setQrRecentPurchases(recent.slice(0, 5));
      }).catch(() => { setQrPreorders([]); setQrRecentPurchases([]); });
  }, [qrSession]);

  const filtered = useMemo(() => menu.filter(m => {
    if (active !== "All" && m.category !== active) return false;
    if (searchQuery && !m.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  }), [menu, active, searchQuery]);
  const cartItems = useMemo(() => Object.entries(cart).map(([id, qty]) => {
    const m = menu.find(x => x.id === id); return m ? { ...m, qty } : null;
  }).filter(Boolean), [cart, menu]);
  const total = cartItems.reduce((s, c) => s + c.price * c.qty, 0);

  // Group cart by vendor so the customer sees the multi-vendor breakdown.
  const cartByVendor = useMemo(() => {
    const groups = {};
    for (const c of cartItems) {
      const key = c.vendor_id || "unassigned";
      groups[key] = groups[key] || { vendor_id: c.vendor_id, items: [], subtotal: 0 };
      groups[key].items.push(c);
      groups[key].subtotal += c.price * c.qty;
    }
    return Object.values(groups);
  }, [cartItems]);

  const inc = (id) => setCart(c => ({ ...c, [id]: (c[id] || 0) + 1 }));
  const dec = (id) => setCart(c => { const n = {...c}; n[id]=(c[id]||0)-1; if (n[id]<=0) delete n[id]; return n; });

  // ---------- Flow handlers ----------
  const onPickCard = () => setFlow("card");
  const onPickGuest = () => setFlow("guest");
  const onPickStudent = () => setFlow("student");
  const onPickStaff = () => setFlow("staff");

  const finishOTPSession = (session, remember) => {
    setQrSession(session);
    localStorage.setItem(QR_TOKEN_KEY, session.token);
    localStorage.setItem(QR_USER_KEY, JSON.stringify(session.user));
    // Soft local expiry — 30 days when "remember me" is on, else 1 hour
    const ms = remember ? 30 * 24 * 60 * 60 * 1000 : 60 * 60 * 1000;
    localStorage.setItem(QR_TOKEN_EXP_KEY, new Date(Date.now() + ms).toISOString());
    localStorage.setItem(QR_REMEMBER_KEY, remember ? "1" : "0");
  };

  const clearOTPSession = () => {
    setQrSession(null);
    localStorage.removeItem(QR_TOKEN_KEY);
    localStorage.removeItem(QR_USER_KEY);
    localStorage.removeItem(QR_TOKEN_EXP_KEY);
    localStorage.removeItem(QR_REMEMBER_KEY);
  };

  // ---------- Order placement ----------
  const placeCardOrder = async () => {
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/qr/order`, {
        rfid_card_id: rfidParam,
        items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })),
        table_number: tableNumber,
      });
      setOrderResult(data.order);
      setCardStudent(data.student);
      setCart({}); setStep("success");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPaying(false); }
  };

  const placeStudentOrder = async (paymentMethod) => {
    if (!qrSession) return toast.error("Sign in with student ID first");
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/qr/student/order`, {
        items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })),
        payment_method: paymentMethod,
        table_number: tableNumber,
      }, { headers: { Authorization: `Bearer ${qrSession.token}` } });
      if (data.needs_payment) {
        await runCashfreeFlow(data, "student");
      } else {
        setOrderResult(data.order);
        setCardStudent({ name: data.user?.name, balance: data.user?.wallet_balance });
        setCart({}); setStep("success");
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPaying(false); }
  };

  const placeStaffOrder = async (paymentMethod) => {
    if (!qrSession) return toast.error("Sign in with staff ID first");
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/qr/staff/order`, {
        items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })),
        payment_method: paymentMethod,
        table_number: tableNumber,
      }, { headers: { Authorization: `Bearer ${qrSession.token}` } });
      if (data.needs_payment) {
        await runCashfreeFlow(data, "staff");
      } else {
        setOrderResult(data.order);
        setCardStudent({ name: data.user?.name, balance: data.user?.points_balance, points: true });
        setCart({}); setStep("success");
      }
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPaying(false); }
  };

  const placeGuestOrder = async () => {
    setPaying(true);
    try {
      const { data: start } = await axios.post(`${API}/qr/guest/start`, {
        items: cartItems.map(c => ({ menu_item_id: c.id, qty: c.qty })),
        ...guestForm, table_number: tableNumber,
      });
      const cfg = await api.get("/payments/config");
      if (cfg.data.cashfree_configured && !start.is_mock && window.Cashfree && start.payment_session_id) {
        const cf = window.Cashfree({ mode: cfg.data.env === "PRODUCTION" ? "production" : "sandbox" });
        const result = await cf.checkout({ paymentSessionId: start.payment_session_id, redirectTarget: "_modal" });
        if (result.error) { toast.error(result.error.message || "Payment cancelled"); setPaying(false); return; }
      } else {
        // Mock path — public mock-success endpoint.
        await axios.post(`${API}/payments/cashfree/mock-success-public/${start.cf_order_id}`);
      }
      const { data: fin } = await axios.post(`${API}/qr/guest/finalize/${start.cf_order_id}`);
      setOrderResult(fin.order);
      setCardStudent({ name: guestForm.customer_name || "Guest", balance: null });
      setCart({}); setStep("success");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setPaying(false); }
  };

  const runCashfreeFlow = async (start, role) => {
    const cfg = await api.get("/payments/config");
    if (cfg.data.cashfree_configured && !start.is_mock && window.Cashfree && start.payment_session_id) {
      const cf = window.Cashfree({ mode: cfg.data.env === "PRODUCTION" ? "production" : "sandbox" });
      const result = await cf.checkout({ paymentSessionId: start.payment_session_id, redirectTarget: "_modal" });
      if (result.error) { toast.error(result.error.message || "Payment cancelled"); return; }
    } else {
      await axios.post(`${API}/payments/cashfree/mock-success-public/${start.cf_order_id}`);
    }
    const path = role === "student" ? "student" : "staff";
    const { data: fin } = await axios.post(`${API}/qr/${path}/finalize/${start.cf_order_id}`);
    setOrderResult(fin.order);
    setCardStudent({ name: qrSession?.user?.name || "Customer" });
    setCart({}); setStep("success");
  };

  // ---------- Render ----------
  return (
    <div className="max-w-md mx-auto min-h-screen bg-cream warm-grain pb-32 relative">
      <div className="sticky top-0 z-20 bg-cream/90 backdrop-blur-md border-b border-border">
        <div className="px-4 h-14 flex items-center justify-between">
          <Link to="/" className="text-ink-600"><ArrowLeft size={18} /></Link>
          <BrandLogo size="sm" />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand bg-brand-50 px-2 py-0.5 rounded-full">QR</span>
        </div>
      </div>

      {(tableNumber || vendorParam || rfidParam || qrSchool) && (
        <div className="px-4 py-2 flex flex-wrap gap-1.5 items-center">
          {qrSchool && (
            <span data-testid="qr-school-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-50 text-brand text-[10px] font-bold">
              <GraduationCap size={10} /> {qrSchool.name}
            </span>
          )}
          {tableNumber && (
            <span data-testid="qr-table-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-ink text-white text-[10px] font-bold">
              <Hash size={10} /> Table {tableNumber}
            </span>
          )}
          {vendorMeta && (
            <span data-testid="qr-vendor-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sun text-ink text-[10px] font-bold">
              <Store size={10} /> {vendorMeta.name}
            </span>
          )}
          {rfidParam && (
            <span data-testid="qr-rfid-badge" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand text-white text-[10px] font-mono font-bold">
              <ScanLine size={10} /> {rfidParam}
            </span>
          )}
          <PWAInstallButton variant="pill" />
        </div>
      )}
      {!(tableNumber || vendorParam || rfidParam || qrSchool) && (
        <div className="px-4 pt-2">
          <PWAInstallButton variant="banner" />
        </div>
      )}

      {step === "success" ? (
        <>
          <SuccessCard order={orderResult} customer={cardStudent} table={tableNumber}
            onAgain={() => { setStep("menu"); setOrderResult(null); }} />
          <PWAInstallNudge trigger="first-order" />
        </>
      ) : flow === "pick" ? (
        <FlowPicker onCard={onPickCard} onGuest={onPickGuest} onStudent={onPickStudent} onStaff={onPickStaff} instashopOn={qrSchool ? qrSchool.instashop_enabled : true} />
      ) : (flow === "student" || flow === "staff") && !qrSession ? (
        <OtpLogin
          mode={flow}
          onBack={() => setFlow("pick")}
          onSession={(s, remember) => finishOTPSession(s, remember)}
        />
      ) : (
        <>
          <ContextHeader flow={flow} qrSession={qrSession} rfid={rfidParam} guestForm={guestForm} setGuestForm={setGuestForm}
            onSwitch={() => { clearOTPSession(); setFlow("pick"); }} planInfo={qrPlanInfo} preorders={qrPreorders} />

          {/* If InstaShop is OFF for this school, show only plan info instead of menu */}
          {(qrSchool && !qrSchool.instashop_enabled && (flow === "student" || flow === "staff")) ? (
            <div className="px-4 py-6 space-y-4">
              <div className="bg-white rounded-2xl border border-border p-5 text-center">
                <ScanLine size={32} className="mx-auto text-brand mb-3" />
                <div className="font-heading text-lg font-bold text-ink">Tap your RFID card at the counter</div>
                <p className="text-sm text-ink-500 mt-2">Your meal plan is validated automatically at the POS terminal. No ordering needed here.</p>
              </div>
              {qrPlanInfo && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4">
                  <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider">Active meal plan</div>
                  <div className="font-heading text-xl font-bold text-ink mt-1">{qrPlanInfo.plan_name}</div>
                  <div className="text-sm text-emerald-700 mt-1">
                    {qrPlanInfo.plan_type === "daily" ? `${qrPlanInfo.used_count || 0}/1 used` : `${qrPlanInfo.used_count || 0} meals · Valid until ${qrPlanInfo.end_date}`}
                  </div>
                </div>
              )}
              {qrPreorders && qrPreorders.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4">
                  <div className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2">Upcoming pre-orders</div>
                  {qrPreorders.map(o => (
                    <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-blue-100 last:border-0">
                      <div>
                        <div className="text-sm font-semibold text-ink">{o.scheduled_date}</div>
                        <div className="text-[11px] text-blue-600">{o.meal_slot} · {(o.items || []).length} items</div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-blue-200 text-blue-800 text-[10px] font-bold">{o.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
          <>
          <div className="px-4 sticky top-14 z-10 bg-cream/90 backdrop-blur-md py-2 space-y-2">
            <input data-testid="qr-search" type="text" placeholder="Search items…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none bg-white" />
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
              {cats.map(c => (
                <button key={c} onClick={() => setActive(c)} data-testid={`qr-cat-${c}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${active === c ? "bg-ink text-white" : "bg-cream-200 text-ink-700"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Recent Purchases */}
          {qrRecentPurchases.length > 0 && (
            <div className="px-4 py-2" data-testid="qr-recent-purchases">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Recent purchases</div>
                {qrRecentPurchases.map(o => (
                  <div key={o.id} className="flex items-center justify-between py-1.5 border-b border-emerald-100 last:border-0">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{(o.items || []).map(i => i.name || i.item_name).join(", ") || "Order"}</div>
                      <div className="text-[11px] text-emerald-600">{o.created_at?.slice(0, 10) || o.scheduled_date}</div>
                    </div>
                    <span className="ml-2 text-sm font-bold text-ink shrink-0">{inr(o.total || 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="px-4 py-3 space-y-3">
            {filtered.map(m => (
              <div key={m.id} className="bg-white rounded-2xl border border-border overflow-hidden flex" data-testid={`qr-item-${m.id}`}>
                <div className="w-24 h-24 bg-cream-200 flex-shrink-0 overflow-hidden">
                  {m.image_url ? <img src={m.image_url} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-ink-400"><ChefHat size={20} /></div>}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-ink truncate">{m.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-ink-500">{m.category}</div>
                    </div>
                    <div className="font-heading font-extrabold text-ink whitespace-nowrap">{inr(m.price)}</div>
                  </div>
                  {m.nutrition && <div className="text-[10px] text-ink-500 mt-1">{m.nutrition.calories} kcal · {m.nutrition.protein}g protein · {m.nutrition.carbs}g carbs · {m.nutrition.fat}g fat</div>}
                  <div className="mt-1 flex flex-wrap gap-1">
                    {m.dietary_tags?.slice(0, 3).map(t => <AllergenBadge key={t} tag={t} />)}
                  </div>
                  <div className="mt-2 flex items-center justify-end">
                    {cart[m.id] ? (
                      <div className="flex items-center gap-2">
                        <button data-testid={`qr-dec-${m.id}`} onClick={() => dec(m.id)} className="w-8 h-8 rounded-full bg-cream-200"><Minus size={14} className="mx-auto" /></button>
                        <span className="font-bold w-5 text-center">{cart[m.id]}</span>
                        <button data-testid={`qr-inc-${m.id}`} onClick={() => inc(m.id)} className="w-8 h-8 rounded-full bg-ink text-white"><Plus size={14} className="mx-auto" /></button>
                      </div>
                    ) : (
                      <button data-testid={`qr-add-${m.id}`} disabled={m.sold_out} onClick={() => inc(m.id)}
                        className="px-4 py-1.5 rounded-full bg-brand text-white text-xs font-bold disabled:opacity-50">
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {cartItems.length > 0 && cartByVendor.length > 1 && (
            <div className="px-4 pb-3" data-testid="qr-multi-vendor-breakdown">
              <div className="rounded-2xl bg-white border border-border p-3 text-xs">
                <div className="font-bold uppercase tracking-wider text-ink-500 mb-1">Single invoice · {cartByVendor.length} kitchens</div>
                {cartByVendor.map((g, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span>{g.items.length} item(s) · {g.vendor_id ? `Vendor ${g.vendor_id.slice(0,4).toUpperCase()}` : "Counter"}</span>
                    <span className="font-bold">{inr(g.subtotal)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {cartItems.length > 0 && (
            <CheckoutBar
              flow={flow} qrSession={qrSession}
              total={total} count={cartItems.reduce((s,c)=>s+c.qty,0)}
              cartItems={cartItems}
              paying={paying}
              onCard={placeCardOrder}
              onStudent={placeStudentOrder}
              onStaff={placeStaffOrder}
              onGuest={placeGuestOrder}
              guestForm={guestForm}
            />
          )}
          </>
          )}
        </>
      )}
    </div>
  );
}

// =================================================================
function FlowPicker({ onCard, onGuest, onStudent, onStaff, instashopOn }) {
  return (
    <div className="px-4 py-7" data-testid="qr-flow-picker">
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Order from QR</div>
      <h1 className="font-heading text-3xl font-extrabold text-ink mt-2">Who's paying?</h1>
      <p className="text-sm text-ink-500 mt-1">Pick the flow that fits — students, staff, parents, or walk-ins.</p>

      <div className="mt-6 space-y-3">
        <FlowCard testid="flow-student" icon={GraduationCap} title="I'm a student" sub="Student ID + parent phone OTP · pay from wallet or Cashfree" onClick={onStudent} accent="bg-brand text-white" />
        <FlowCard testid="flow-staff" icon={Briefcase} title="I'm staff / faculty" sub="Staff ID + phone OTP · pay with points or cash; Cashfree available" onClick={onStaff} accent="bg-ink text-white" />
        {instashopOn && <FlowCard testid="flow-guest" icon={UserCircle2} title="Walk-in / parent guest" sub="No login · pay directly with Cashfree (UPI, card, netbanking)" onClick={onGuest} accent="bg-sun text-ink" />}
      </div>

      <div className="mt-7 text-[11px] text-ink-500 leading-relaxed">
        Parents — to use the student wallet, sign in via "I'm a student" with the student's ID. The OTP is sent to the registered parent phone.
      </div>
    </div>
  );
}

function FlowCard({ icon: Icon, title, sub, onClick, accent, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="w-full text-left bg-white rounded-3xl border border-border p-5 hover:border-brand active:scale-[0.99] transition-all">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${accent}`}><Icon size={22} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-lg font-bold text-ink">{title}</div>
          <div className="text-xs text-ink-500 mt-0.5">{sub}</div>
        </div>
        <ChevronRight size={18} className="text-ink-400" />
      </div>
    </button>
  );
}

// =================================================================
function OtpLogin({ mode, onBack, onSession }) {
  const [identifier, setIdentifier] = useState("");
  const [stage, setStage] = useState("enter"); // enter | otp
  const [otp, setOtp] = useState("");
  const [info, setInfo] = useState(null); // { name, phone_masked, dev_code? }
  const [loading, setLoading] = useState(false);
  const [remember, setRemember] = useState(true);  // default ON — most QR users want quick repeat ordering

  const requestOtp = async () => {
    if (!identifier.trim()) return toast.error("Enter your ID");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/request`, {
        identifier_type: mode, identifier: identifier.trim(),
      });
      setInfo(data); setStage("otp");
      if (data.dev_code) toast.info(`Dev OTP: ${data.dev_code}`, { duration: 8000 });
      else toast.success(`OTP sent to ${data.phone_masked}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  const verify = async () => {
    if (!otp.trim()) return toast.error("Enter the OTP");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/verify`, {
        identifier_type: mode, identifier: identifier.trim(), otp: otp.trim(), remember,
      });
      onSession({ token: data.qr_token, user: data.user }, remember);
      toast.success(`Welcome ${data.user.name}`);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-4 py-7" data-testid={`qr-otp-${mode}`}>
      <button onClick={onBack} className="text-sm text-ink-500 inline-flex items-center gap-1"><ArrowLeft size={14}/> Back</button>
      <h1 className="font-heading text-2xl font-extrabold text-ink mt-3">{mode === "student" ? "Student sign-in" : "Staff sign-in"}</h1>
      <p className="text-sm text-ink-500 mt-1">
        {mode === "student"
          ? "Enter the student ID. We'll send an OTP to the parent's registered phone."
          : "Enter your staff ID. We'll send an OTP to your registered phone."}
      </p>

      {stage === "enter" ? (
        <div className="mt-6 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-500">{mode === "student" ? "Student ID" : "Staff ID"}</label>
            <input data-testid="otp-identifier" autoFocus value={identifier}
              onChange={e => setIdentifier(e.target.value)} placeholder={mode === "student" ? "STU001" : "EMP001"}
              className="mt-1 w-full rounded-xl border-2 border-border px-4 py-3 text-base font-mono uppercase focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
          </div>
          <button data-testid="otp-send" onClick={requestOtp} disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand text-white font-semibold disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <KeyRound size={16}/>} Send OTP
          </button>
          <div className="text-[11px] text-ink-500 mt-2">
            {mode === "student" ? <>Try <span className="font-mono">STU001</span> (Aarav)</> : <>Try <span className="font-mono">EMP001</span> (Vikram)</>}
          </div>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          <div className="bg-cream-100 rounded-2xl p-4 text-sm">
            <div className="font-semibold text-ink">{info?.name}</div>
            <div className="text-ink-500 text-xs mt-0.5">OTP sent to {info?.phone_masked}</div>
            {info?.dev_code && <div className="text-[11px] text-amber-700 mt-1">Dev mode — code: <span className="font-mono font-bold">{info.dev_code}</span></div>}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-ink-500">6-digit code</label>
            <input data-testid="otp-code" inputMode="numeric" maxLength={6} value={otp}
              onChange={e => setOtp(e.target.value.replace(/\D/g,""))} placeholder="••••••"
              className="mt-1 w-full rounded-xl border-2 border-border px-4 py-3 text-2xl font-bold text-center tracking-[0.4em] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20" />
          </div>
          <button data-testid="otp-verify" onClick={verify} disabled={loading || otp.length < 4}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-ink text-white font-semibold disabled:opacity-60">
            {loading ? <Loader2 size={16} className="animate-spin"/> : <ShieldCheck size={16}/>} Verify & continue
          </button>
          <label className="flex items-center gap-2 text-xs text-ink-700 mt-2 cursor-pointer">
            <input data-testid="otp-remember" type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} className="accent-brand" />
            Remember me on this device for 30 days
          </label>
          <button onClick={() => setStage("enter")} className="w-full text-xs text-ink-500 underline mt-1">Use a different ID</button>
        </div>
      )}
    </div>
  );
}

// =================================================================
function ContextHeader({ flow, qrSession, rfid, guestForm, setGuestForm, onSwitch, planInfo, preorders }) {
  if (flow === "card") {
    return (
      <div className="px-4 py-5">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Card mode</div>
        <div className="font-heading text-2xl font-extrabold text-ink mt-1">Pick what you want</div>
        <div className="mt-2 text-sm text-ink-600">Charged to <span className="font-mono font-bold">{rfid}</span> · no login.</div>
      </div>
    );
  }
  if (flow === "guest") {
    return (
      <div className="px-4 py-5">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Walk-in / Guest</div>
            <div className="font-heading text-2xl font-extrabold text-ink mt-1">Pay direct via Cashfree</div>
          </div>
          <button onClick={onSwitch} className="text-[11px] text-ink-500 underline">Switch flow</button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <input data-testid="guest-name" value={guestForm.customer_name} onChange={e => setGuestForm({...guestForm, customer_name: e.target.value})}
            placeholder="Your name *" required className="rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
          <input data-testid="guest-phone" value={guestForm.customer_phone} onChange={e => setGuestForm({...guestForm, customer_phone: e.target.value})}
            placeholder="Phone *" required className="rounded-xl border border-border px-3 py-2 text-sm focus:border-brand focus:outline-none" />
        </div>
        <div className="mt-1 text-[10px] text-ink-500 italic">* Name and phone are required for the receipt.</div>
      </div>
    );
  }
  // student/staff signed-in
  const u = qrSession?.user;
  const isStaff = flow === "staff";
  return (
    <div className="px-4 py-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">{isStaff ? "Staff" : "Student"} sign-in</div>
          <div className="font-heading text-xl font-extrabold text-ink mt-1">Hi {u?.name?.split(" ")[0]}</div>
          {u?.grade && <div className="text-xs text-ink-500">{u.grade}</div>}
        </div>
        <button data-testid="qr-switch-flow" onClick={onSwitch} className="text-[11px] text-ink-500 underline">Switch flow</button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        {!isStaff && (
          <span className="inline-flex items-center gap-1 bg-white border border-border rounded-full px-3 py-1">
            <Wallet size={12} className="text-brand" /> <b>{inr(u?.wallet_balance || 0)}</b> <span className="text-ink-500 text-xs">wallet</span>
          </span>
        )}
        {isStaff && (
          <span className="inline-flex items-center gap-1 bg-white border border-border rounded-full px-3 py-1">
            <Coins size={12} className="text-amber-600" /> <b>{(u?.points_balance || 0).toFixed(0)}</b> <span className="text-ink-500 text-xs">points</span>
          </span>
        )}
      </div>
      {/* Active Meal Plan */}
      {planInfo && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="qr-meal-plan">
          <div className="flex items-center justify-between">
            <div className="text-xs font-bold text-emerald-700">{planInfo.plan_name || "Active Plan"}</div>
            <div className="text-xs font-bold text-emerald-800">{planInfo.used_count ?? 0}/{planInfo.plan_type === "daily" ? 1 : (planInfo.total_days || "—")}</div>
          </div>
          <div className="text-[10px] text-emerald-600 mt-0.5">
            {planInfo.plan_type === "daily" ? "Single meal plan" : `Valid until ${planInfo.end_date}`}
          </div>
        </div>
      )}
      {/* Upcoming Pre-orders */}
      {preorders && preorders.length > 0 && (
        <div className="mt-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2" data-testid="qr-preorders">
          <div className="text-xs font-bold text-blue-700 mb-1">Upcoming pre-orders</div>
          {preorders.map(o => (
            <div key={o.id} className="flex items-center justify-between text-[11px] text-blue-800">
              <span>{o.scheduled_date} · {o.meal_slot}</span>
              <span className="font-bold">{(o.items || []).length} items</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =================================================================
function CheckoutBar({ flow, qrSession, total, count, onCard, onStudent, onStaff, onGuest, paying, guestForm, cartItems }) {
  const [open, setOpen] = useState(false);
  const u = qrSession?.user;
  const wallet = u?.wallet_balance || 0;
  const points = u?.points_balance || 0;
  const walletCovers = wallet >= total;
  const pointsCovers = points >= total;

  const hideSheet = () => setOpen(false);

  // Simple compact pill (always visible) + slide-up sheet on tap
  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-30 px-4 pb-4 pt-3 bg-gradient-to-t from-cream via-cream/95 to-transparent">
        <button data-testid="qr-checkout-open" onClick={() => setOpen(true)} disabled={paying}
          className="w-full max-w-md mx-auto bg-ink text-white rounded-2xl shadow-floating px-4 py-3.5 flex items-center justify-between gap-3 active:scale-[0.99] transition-transform disabled:opacity-60">
          <div className="flex items-center gap-2">
            <span className="bg-sun text-ink rounded-full w-7 h-7 flex items-center justify-center text-xs font-extrabold">{count}</span>
            <span className="font-bold text-sm">View order</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-heading text-xl font-extrabold">{inr(total)}</span>
            <ChevronRight size={18} />
          </div>
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-40 bg-ink/40 flex items-end" onClick={hideSheet} data-testid="qr-checkout-sheet">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md mx-auto bg-white rounded-t-3xl pb-4 pt-2 max-h-[85vh] overflow-y-auto">
            <div className="flex justify-center pb-2"><div className="w-12 h-1.5 rounded-full bg-cream-300"/></div>

            <div className="px-5 pb-3 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand">Checkout</div>
                <div className="font-heading text-2xl font-extrabold text-ink mt-1">Confirm &amp; pay</div>
                <div className="text-xs text-ink-500 mt-0.5">Order &amp; eat right away — preorders are optional from the menu.</div>
              </div>
              <button onClick={hideSheet} className="text-ink-500 p-1"><X size={20} /></button>
            </div>

            {/* order recap */}
            <div className="px-5 pt-1 pb-3">
              <div className="bg-cream-100 rounded-2xl p-4 max-h-44 overflow-y-auto">
                {cartItems.map(it => (
                  <div key={it.id} className="flex justify-between text-sm py-1">
                    <span className="text-ink-700"><span className="font-bold mr-2">{it.qty}×</span>{it.name}</span>
                    <span className="font-bold">{inr(it.price * it.qty)}</span>
                  </div>
                ))}
                <div className="mt-2 pt-2 border-t border-border flex justify-between text-base font-extrabold">
                  <span>Total</span>
                  <span className="font-heading">{inr(total)}</span>
                </div>
              </div>
            </div>

            {/* payment methods */}
            {flow === "card" && (
              <div className="px-5 pb-5">
                <button data-testid="qr-pay-card" disabled={paying} onClick={onCard}
                  className="w-full px-5 py-4 rounded-2xl bg-brand text-white font-bold text-base disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  <ScanLine size={18}/> {paying ? "Charging…" : `Charge RFID · ${inr(total)}`}
                </button>
                <div className="text-[11px] text-ink-500 text-center mt-2">Wallet linked to this card pays the bill.</div>
              </div>
            )}

            {flow === "guest" && (
              <div className="px-5 pb-5">
                <button data-testid="qr-pay-guest" disabled={paying || !(guestForm.customer_phone || "").trim() || !(guestForm.customer_name || "").trim()} onClick={onGuest}
                  className="w-full px-5 py-4 rounded-2xl bg-brand text-white font-bold text-base disabled:opacity-60 inline-flex items-center justify-center gap-2">
                  <CreditCard size={18}/> {paying ? "Processing…" : `Pay ${inr(total)} · UPI / Card`}
                </button>
                <div className="text-[11px] text-ink-500 text-center mt-2">Powered by Cashfree · UPI · Card · Netbanking</div>
              </div>
            )}

            {flow === "student" && qrSession && (
              <div className="px-5 pb-5 space-y-3">
                {/* Wallet balance — primary */}
                <button data-testid="qr-pay-wallet" disabled={paying || !walletCovers} onClick={() => { hideSheet(); onStudent("wallet"); }}
                  className={`w-full text-left rounded-2xl p-4 border-2 transition-all ${walletCovers ? "border-brand bg-brand text-white" : "border-border bg-white opacity-60 cursor-not-allowed"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${walletCovers ? "bg-white/20" : "bg-cream-200"}`}><Wallet size={20}/></div>
                      <div>
                        <div className="font-heading font-extrabold text-base flex items-center gap-1.5">Pay from wallet {walletCovers && <span className="bg-sun text-ink text-[9px] px-1.5 py-0.5 rounded-full font-bold">RECOMMENDED</span>}</div>
                        <div className={`text-xs mt-0.5 ${walletCovers ? "text-white/85" : "text-ink-500"}`}>Balance: <b>{inr(wallet)}</b>{!walletCovers && <span className="ml-1">— short by {inr(total - wallet)}</span>}</div>
                      </div>
                    </div>
                    {walletCovers && <ChevronRight size={20}/>}
                  </div>
                </button>

                <div className="text-[10px] uppercase tracking-wider text-ink-500 text-center font-bold">{walletCovers ? "Or pay with another method" : "Top-up + checkout in one step"}</div>

                <button data-testid="qr-pay-cashfree" disabled={paying} onClick={() => { hideSheet(); onStudent("cashfree"); }}
                  className="w-full text-left rounded-2xl p-4 border border-border bg-white hover:border-ink transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-cream-200 flex items-center justify-center"><CreditCard size={20}/></div>
                      <div>
                        <div className="font-heading font-bold">UPI · Card · Netbanking</div>
                        <div className="text-xs text-ink-500 mt-0.5">Powered by Cashfree</div>
                      </div>
                    </div>
                    <ChevronRight size={18}/>
                  </div>
                </button>
              </div>
            )}

            {flow === "staff" && qrSession && (
              <div className="px-5 pb-5 space-y-3">
                <button data-testid="qr-pay-points" disabled={paying || !pointsCovers} onClick={() => { hideSheet(); onStaff("points"); }}
                  className={`w-full text-left rounded-2xl p-4 border-2 transition-all ${pointsCovers ? "border-amber-500 bg-amber-500 text-white" : "border-border bg-white opacity-60 cursor-not-allowed"}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pointsCovers ? "bg-white/20" : "bg-cream-200"}`}><Coins size={20}/></div>
                      <div>
                        <div className="font-heading font-extrabold flex items-center gap-1.5">Pay with points {pointsCovers && <span className="bg-sun text-ink text-[9px] px-1.5 py-0.5 rounded-full font-bold">RECOMMENDED</span>}</div>
                        <div className={`text-xs mt-0.5 ${pointsCovers ? "text-white/85" : "text-ink-500"}`}>Balance: <b>{points.toFixed(0)}</b> pts{!pointsCovers && <span className="ml-1">— short by {(total - points).toFixed(0)} pts</span>}</div>
                      </div>
                    </div>
                    {pointsCovers && <ChevronRight size={20}/>}
                  </div>
                </button>

                <div className="text-[10px] uppercase tracking-wider text-ink-500 text-center font-bold">Or</div>

                <div className="grid grid-cols-2 gap-3">
                  <button data-testid="qr-pay-cash" disabled={paying} onClick={() => { hideSheet(); onStaff("cash"); }}
                    className="rounded-2xl p-4 border border-border bg-white hover:border-ink text-left">
                    <div className="w-10 h-10 rounded-xl bg-cream-200 flex items-center justify-center mb-2"><BadgeIndianRupee size={18}/></div>
                    <div className="font-bold text-sm">Cash at counter</div>
                    <div className="text-[11px] text-ink-500">Cashier collects later</div>
                  </button>
                  <button data-testid="qr-pay-cashfree-staff" disabled={paying} onClick={() => { hideSheet(); onStaff("cashfree"); }}
                    className="rounded-2xl p-4 border border-border bg-white hover:border-ink text-left">
                    <div className="w-10 h-10 rounded-xl bg-cream-200 flex items-center justify-center mb-2"><CreditCard size={18}/></div>
                    <div className="font-bold text-sm">UPI / Card</div>
                    <div className="text-[11px] text-ink-500">Cashfree</div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// =================================================================
function SuccessCard({ order, customer, table, onAgain }) {
  const [invoice, setInvoice] = useState(null);
  const [showFull, setShowFull] = useState(false);

  useEffect(() => {
    if (!order?.id) return;
    // Public-OK because qr_session token is sent. If 401, just skip the breakdown.
    api.get(`/orders/${order.id}/invoice`)
      .then(r => setInvoice(r.data))
      .catch(() => {});
  }, [order?.id]);

  const t = invoice?.totals;
  const channel = invoice?.channel || order?.channel || order?.type || "QR order";

  return (
    <div className="px-4 py-8" data-testid="qr-success">
      <div className="bg-white rounded-3xl p-6 sm:p-7 border border-border shadow-soft">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-emerald-50 flex items-center justify-center text-emerald-700">
            <CheckCircle2 size={32} />
          </div>
          <div className="font-heading text-2xl font-bold text-ink mt-4">Order placed!</div>
          <div className="text-sm text-ink-500 mt-1">
            {customer?.points
              ? `Charged to ${customer.name} · points balance ${customer.balance?.toFixed(0)}`
              : customer?.balance != null
                ? `Charged to ${customer.name} · balance ${inr(customer.balance)}`
                : `Thanks ${customer?.name || "guest"} — we'll buzz when ready.`}
            {table && ` · Table ${table}`}
          </div>
          {(customer?.phone || invoice?.buyer?.phone) && (
            <div className="text-[11px] text-emerald-700 bg-emerald-50 rounded-full px-3 py-1 inline-block mt-3">
              Receipt sent to SMS &amp; WhatsApp · {(invoice?.buyer?.phone || customer?.phone || "").replace(/.(?=.{4})/g, "·")}
            </div>
          )}
        </div>

        {/* School-branded invoice header */}
        {invoice?.issuer?.name && (
          <div className="mt-6 pb-4 border-b border-border" data-testid="qr-invoice-header">
            <div className="font-heading text-sm font-extrabold text-ink leading-tight">{invoice.issuer.name}</div>
            {invoice.issuer.address && <div className="text-[10px] text-ink-500 leading-snug mt-0.5">{invoice.issuer.address}</div>}
            {invoice.issuer.gstin && <div className="text-[10px] text-ink-500 mt-0.5 font-mono">GSTIN: {invoice.issuer.gstin}</div>}
            <div className="text-[10px] text-ink-500 mt-1">Invoice <span className="font-mono font-bold text-ink">{invoice.invoice_no}</span> · {new Date(invoice.issued_at).toLocaleString()}</div>
          </div>
        )}

        <div className="mt-4 bg-cream-100 rounded-2xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-ink-500 font-bold flex items-center justify-between">
            <span>Order #{order?.id?.slice(0, 8).toUpperCase()}</span>
            <span>{channel}</span>
          </div>
          {order?.kots?.length > 1 && (
            <div className="mt-2 text-[11px] text-amber-700 bg-amber-50 rounded p-2 inline-block">
              Single invoice → {order.kots.length} kitchens (separate KOTs sent to each)
            </div>
          )}
          <div className="mt-3 space-y-1.5">
            {(invoice?.items || order?.items || []).map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-ink-700"><b className="mr-1">{i.qty}×</b>{i.name}{i.gst_pct ? <span className="text-[10px] text-ink-500 ml-1">@ {i.gst_pct}% GST</span> : null}</span>
                <span className="font-bold">{inr(i.subtotal != null ? (i.subtotal + (i.tax || 0)) : (i.price * i.qty))}</span>
              </div>
            ))}
          </div>

          {t ? (
            <div className="mt-3 pt-3 border-t border-border space-y-1 text-sm" data-testid="qr-tax-breakdown">
              <Row label="Subtotal (taxable)" value={inr(t.subtotal)} />
              <Row label={`CGST (₹${t.cgst.toFixed(2)})`} value={inr(t.cgst)} />
              <Row label={`SGST (₹${t.sgst.toFixed(2)})`} value={inr(t.sgst)} />
              <div className="flex justify-between font-extrabold pt-2 border-t border-border text-base"><span>Grand total</span><span>{inr(t.grand_total)}</span></div>
            </div>
          ) : (
            <div className="border-t border-border mt-3 pt-3 flex justify-between font-bold">
              <span>Total</span><span>{inr(order?.total)}</span>
            </div>
          )}

          {order?.cash_due > 0 && (
            <div className="mt-3 text-xs text-amber-800 bg-amber-50 rounded p-2">
              Cash due at counter: <b>{inr(order.cash_due)}</b>
            </div>
          )}

          {invoice?.footer && (
            <div className="mt-3 pt-3 border-t border-border text-[10px] text-ink-500 text-center">{invoice.footer}</div>
          )}
        </div>

        <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
          <button data-testid="qr-print-receipt" onClick={() => window.print()}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-cream-200 text-ink font-semibold">
            <Printer size={14}/> Print
          </button>
          <button data-testid="qr-new-order" onClick={onAgain}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold">
            Order again <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-ink-600"><span className="text-xs">{label}</span><span className="font-bold text-sm">{value}</span></div>
);
